import { useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MailX, CheckCircle, Loader2, AlertCircle, Home } from "lucide-react";

interface UnsubscribeInfo {
  alreadyUnsubscribed: boolean;
  businessName: string;
  email: string;
  token?: string;
}

interface UnsubscribeResult {
  success: boolean;
  message: string;
  businessName: string;
}

export default function Unsubscribe() {
  const { token } = useParams<{ token: string }>();
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);

  const { data: info, isLoading, error } = useQuery<UnsubscribeInfo>({
    queryKey: ["/api/public/unsubscribe", token],
    queryFn: async () => {
      const res = await fetch(`/api/public/unsubscribe/${token}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error?.message || "Invalid unsubscribe link");
      }
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  const unsubscribeMutation = useMutation<UnsubscribeResult, Error>({
    mutationFn: async () => {
      const res = await fetch("/api/public/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error?.message || "Failed to unsubscribe");
      }
      return res.json();
    },
    onSuccess: () => {
      setIsUnsubscribed(true);
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state (invalid link)
  if (error || !info) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mb-6">
              <AlertCircle className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
            <p className="text-muted-foreground mb-6">
              This unsubscribe link is invalid or has expired. If you continue to receive unwanted emails, please contact the business directly.
            </p>
            <Link href="/">
              <Button variant="outline" className="gap-2">
                <Home className="w-4 h-4" />
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already unsubscribed or just unsubscribed
  if (info.alreadyUnsubscribed || isUnsubscribed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-6">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Unsubscribed</h1>
            <p className="text-muted-foreground mb-6">
              <strong>{info.email}</strong> has been unsubscribed from review request emails from <strong>{info.businessName}</strong>.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              You will no longer receive review request emails from this business.
            </p>
            <Link href="/">
              <Button variant="outline" className="gap-2">
                <Home className="w-4 h-4" />
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Confirmation screen
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mx-auto mb-4">
            <MailX className="w-8 h-8 text-muted-foreground" />
          </div>
          <CardTitle>Unsubscribe from Emails</CardTitle>
          <CardDescription>
            You're about to unsubscribe from review request emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Email</p>
            <p className="font-medium">{info.email}</p>
            <p className="text-sm text-muted-foreground mt-3 mb-1">From</p>
            <p className="font-medium">{info.businessName}</p>
          </div>

          {unsubscribeMutation.error && (
            <Alert variant="destructive">
              <AlertDescription>
                {unsubscribeMutation.error.message}
              </AlertDescription>
            </Alert>
          )}

          <Button
            onClick={() => unsubscribeMutation.mutate()}
            disabled={unsubscribeMutation.isPending}
            className="w-full"
            variant="destructive"
          >
            {unsubscribeMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Unsubscribing...
              </>
            ) : (
              "Confirm Unsubscribe"
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            You can still receive emails if you submit a new review request form.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}




